var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Tabs, Tab, useMediaQuery, useTheme, Skeleton, Tooltip, } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContainer, PageContent } from "@ui/components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { GridFilters } from "@ui/components/grid/GridFilters";
import { PrimaryGrid } from "@ui/components/grid/PrimaryGrid";
import TemperatureDisplay from "@ui/components/temperature/TemperatureDisplay";
import { TemperatureStatusBadge, getNodeTemperatureStatus, } from "@ui/components/temperature";
import StatusIndicator from "@ui/components/status/StatusIndicator";
import { SButton } from "@ui/components/buttons/SButton";
import { colCenter } from "@ui/components/style/style";
import { ConnectedIcon, DisconnectedIcon, } from "@ui/components/icons/ConnectionIcons";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { useTranslation } from "react-i18next";
import { GridCellStack } from "@ui/components/grid";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
import { getLatestApActivityIso } from "@utility/access-point-monitoring";
import { APDetailsCard } from "./components/APDetailsCard";
import { APReadingsSection, READING_COLUMN_WIDTH } from "./components/APReadingsSection";
import { SensorAlarmMarker } from "./components/SensorAlarmMarker";
import { SensorReading } from "./components/SensorReading";
import { SensorType, isSensorHidden } from "@ui/utils/sensors";
import { limitReasons, worstRowStatus } from "@ui/utils/sensor-limit-status";
import { connectedNodeTileColor, toConnectedNodeDisplay } from "./connectedNodeDisplay";
import { DeviceDetailsRow, DeviceStatusIndicator, TemperatureStatusChip, getDeviceDetailsMinWidth, } from "@ui/components/grid/DeviceDetailsRow";
import { hasEnvironmentReadings } from "@ui/utils/environment-readings";
import { NodeActionsDialog } from "./components/NodeActionsDialog";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAccessPoint } from "@reducers/access-points/thunk/access-point-thunk";
import { getNodesByAccessPointUid, getAllNodes, forceConnectNode, forceDisconnectNode, replaceNodeV2, } from "@reducers/nodes/thunk/nodes-thunk";
import { toast } from "@ui/utils/toaster";
import { APDetailsCardSkeleton } from "./components/APDetailsCardSkeleton";
import { APDetailsNodeCard } from "./components/APDetailsNodeCard";
import { LegacyAPDetailsNodeCard } from "./components/LegacyAPDetailsNodeCard";
import localStorageAccessor from "@utility/local-storage-accessor";
import { NodeBehaviour } from "@reducers/nodes/models/nodes-dto";
import { useDebounce } from "@ui/utils/useDebounce";
import { cleanNodesFiltersForApi } from "./cleanNodesFiltersForApi";
import { Icon } from "@iconify/react";
import { FreeRoamingBadge } from "@ui/components/badges/FreeRoamingBadge";
import { alarmTypeLabel } from "@ui/utils/alarm-type-label";
export const AppDetails = () => {
    var _a, _b, _c;
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { apId } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // Mirrors PrimaryGrid: below lg the node list switches from table rows to cards.
    const isCardView = useMediaQuery(theme.breakpoints.down("lg"));
    const dispatch = useAppDispatch();
    // Get AP data from Redux
    const { selectedAccessPoint, singleLoading, singleError } = useAppSelector((state) => state.accessPoint);
    // Get nodes data from Redux
    const { nodes, loading: nodesLoading, paginationInfo, } = useAppSelector((state) => state.nodes);
    // Local state - try to get cached AP first
    const [cachedAp, setCachedAp] = useState(() => localStorageAccessor.getSelectedAccessPoint());
    // Get the effective AP data (from Redux or cache)
    const effectiveAp = selectedAccessPoint || cachedAp;
    // New hardware gets the AP readings row and the new node rows; every other access point keeps
    // the original layout. Only trust the flag from the AP this page is showing (the cached AP
    // can belong to a previously opened one), and treat "not loaded yet" as the original layout.
    const isNewUi = Boolean((effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.isNewHardwareVariant) &&
        ((_a = effectiveAp.accessPointUID) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === (apId === null || apId === void 0 ? void 0 : apId.toLowerCase()));
    // Tab and filters state - declared before useEffect that uses it
    const [tabValue, setTabValue] = useState("connected");
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(25);
    const cleanFilters = useCallback(cleanNodesFiltersForApi, []);
    // Fetch nodes when filters, search, or tab changes (guard against StrictMode double-invoke)
    const nodesFetchKeyRef = useRef(null);
    // Always reflects the latest tabValue — readable inside async callbacks without stale closure
    const tabValueRef = useRef(tabValue);
    // Incrementing this forces the fetch effect to re-run (used to recover from stale responses)
    const [forceFetchTick, setForceFetchTick] = useState(0);
    useEffect(() => {
        tabValueRef.current = tabValue;
    }, [tabValue]);
    useEffect(() => {
        if (!apId)
            return;
        const apiFilters = cleanFilters(filters);
        // Add global search
        if (debouncedSearchTerm) {
            apiFilters.globalSearch = debouncedSearchTerm;
        }
        // Build a key from all dependencies to detect true changes vs StrictMode re-runs
        const fetchKey = JSON.stringify({
            apId,
            tabValue,
            filters,
            debouncedSearchTerm,
            currentPage,
            currentPageSize,
        });
        if (nodesFetchKeyRef.current === fetchKey)
            return;
        nodesFetchKeyRef.current = fetchKey;
        const capturedTab = tabValue;
        // After a fetch completes, check whether the user has already switched tabs.
        // If so, the Redux `nodes` state now contains stale data for the wrong tab.
        // Reset the key guard and bump forceFetchTick so this effect re-fires and
        // overwrites Redux with the correct tab's data.
        const afterFetch = () => {
            if (tabValueRef.current !== capturedTab) {
                nodesFetchKeyRef.current = null;
                setForceFetchTick((t) => t + 1);
            }
        };
        if (tabValue === "disconnected") {
            // Disconnected nodes (all APs): server-side pagination
            dispatch(getAllNodes({
                pageNumber: currentPage,
                pageSize: currentPageSize,
                filters: Object.assign(Object.assign({}, apiFilters), { simulatedDisconnectedOnly: true }),
            })).then(afterFetch).catch(() => { });
        }
        else {
            // Fetch nodes for this specific AP
            dispatch(getNodesByAccessPointUid({
                accessPointUid: apId,
                query: {
                    pageNumber: currentPage,
                    pageSize: currentPageSize,
                    filters: apiFilters,
                },
            })).then(afterFetch).catch(() => { });
        }
    }, [
        dispatch,
        apId,
        tabValue,
        filters,
        debouncedSearchTerm,
        cleanFilters,
        currentPage,
        currentPageSize,
        forceFetchTick,
    ]);
    // Fetch AP data on mount (guard against StrictMode double-invoke)
    const apFetchedRef = useRef(null);
    useEffect(() => {
        if (apId && apFetchedRef.current !== apId) {
            apFetchedRef.current = apId;
            dispatch(getAccessPoint(apId));
        }
    }, [dispatch, apId]);
    // Cache AP data when it's loaded from Redux
    useEffect(() => {
        if (selectedAccessPoint) {
            localStorageAccessor.setSelectedAccessPoint(selectedAccessPoint);
            setCachedAp(selectedAccessPoint);
        }
    }, [selectedAccessPoint]);
    // Handle search change
    const handleSearchChange = useCallback((event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    }, []);
    // Handle filter change
    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
    }, []);
    // State for dialogs
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    // Transform API nodes data to grid format
    const apNodes = React.useMemo(() => {
        if (!nodes)
            return [];
        return nodes.map((node) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            // A relayed sensor with only its external temperature on shows that one as its main temperature (see connectedNodeDisplay).
            const display = toConnectedNodeDisplay(node, t);
            return {
                id: node.id,
                temperature: display.nodeTemperature,
                nodeTemperature: display.nodeTemperature,
                externalTemperature: (_a = node.externalTemperature) !== null && _a !== void 0 ? _a : null,
                humidity: (_b = node.humidity) !== null && _b !== void 0 ? _b : null,
                lowTemperature: display.lowTemperature,
                highTemperature: display.highTemperature,
                externalAsMain: display.externalAsMain,
                title: display.title,
                subtitle: display.subtitle,
                externalTooltip: display.externalTooltip,
                tileInfoTooltip: display.tileInfoTooltip,
                nodeTypeCode: node.nodeTypeCode,
                leftIndicator: node.nodeTypeCode === 1 ? 1 : undefined,
                rightIndicator: node.nodeTypeCode === 1 ? 2 : undefined,
                nodeName: node.nodeName,
                nodeId: node.nodeUid,
                isActive: node.isActive,
                status: node.isActive && node.nodeBehaviour == NodeBehaviour.Normal
                    ? "Active"
                    : "Inactive",
                deviceName: node.accessPointName || "",
                deviceId: node.accessPointUid || "",
                lastUpdate: node.lastUpdated,
                nodeBehaviour: (_c = node.nodeBehaviour) !== null && _c !== void 0 ? _c : NodeBehaviour.Normal,
                lastAlarm: (_d = node.lastAlarm) !== null && _d !== void 0 ? _d : null,
                lastAlarms: (_e = node.lastAlarms) !== null && _e !== void 0 ? _e : [],
                sensorStatuses: (_f = node.sensorStatuses) !== null && _f !== void 0 ? _f : [],
                disabledSensors: (_g = node.disabledSensors) !== null && _g !== void 0 ? _g : [],
                freeRoamingNode: (_h = node.freeRoamingNode) !== null && _h !== void 0 ? _h : false,
            };
        });
    }, [nodes, t]);
    const lastAlarmTooltipContent = useCallback((a) => (React.createElement(Box, { sx: { py: 0.5, maxWidth: 260 } },
        React.createElement(Typography, { variant: "subtitle2", sx: { mb: 0.75, fontWeight: 600 } }, t("common:grid.lastAlarm")),
        React.createElement(Typography, { variant: "body2", sx: { display: "block", mb: 0.5 } },
            t("common:grid.alarmType"),
            ": ",
            alarmTypeLabel(a.alarmType, t, a.sensorType)),
        React.createElement(Typography, { variant: "body2", sx: { display: "block", mb: 0.5 } },
            t("common:grid.temperatureAtAlarm"),
            ":",
            " ",
            a.temperature != null && !Number.isNaN(Number(a.temperature))
                ? `${Number(a.temperature).toFixed(1)}°`
                : t("common:grid.noTemperatureForAlarm")),
        React.createElement(Typography, { variant: "body2", sx: { display: "block", mb: 0.5 } },
            t("common:grid.alarmTime"),
            ":",
            " ",
            formatUtcToLocalDate(a.alarmStartDateTime),
            " ",
            formatUtcToLocalTime(a.alarmStartDateTime)),
        React.createElement(Typography, { variant: "body2", sx: { display: "block" } },
            t("common:grid.acknowledgementStatus"),
            ":",
            " ",
            a.isAlarmAcknowledged
                ? t("common:grid.acknowledged")
                : t("common:grid.unacknowledged")))), [t]);
    // Layout props per UI: [new, original]
    const layout = (newUi, original) => (isNewUi ? newUi : original);
    const columns = [
        Object.assign(Object.assign({ field: "node", headerName: t("grid.ap") }, layout({ width: 123 }, { minWidth: 100 })), { filterable: false, type: "string", renderCell: (params) => (React.createElement(TemperatureDisplay, { width: 91, temperature: params.row.nodeTemperature, color: params.row.nodeTypeCode === 1 ? "#1D95E2" : connectedNodeTileColor(params.row), external: params.row.externalAsMain, infoTooltip: params.row.tileInfoTooltip, leftIndicator: params.row.leftIndicator, rightIndicator: params.row.rightIndicator })) }),
        Object.assign(Object.assign({ field: "nodeName", headerName: t("grid.nameId") }, layout({ flex: 4, minWidth: 240 }, { flex: 1.5, minWidth: 180 })), { filterable: true, filterType: "text", type: "string", renderCell: (params) => {
                var _a, _b;
                const alarm = params.row.lastAlarm;
                const sensorAlarms = ((_a = params.row.lastAlarms) !== null && _a !== void 0 ? _a : []);
                const showAlarmMarker = tabValue === "connected" &&
                    alarm != null &&
                    params.row.nodeTypeCode !== 1;
                return (
                // width: 100% is what lets the name box below actually claim the leftover
                // width, so the badge lands at the same x position on every row.
                React.createElement(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        height: "100%",
                        width: "100%",
                        minWidth: 0,
                    } },
                    React.createElement(Box, { sx: { minWidth: 0, flex: 1 } }, isNewUi ? (React.createElement(Tooltip, { title: (_b = params.row.title) !== null && _b !== void 0 ? _b : "" },
                        React.createElement(Box, null,
                            React.createElement(GridCellStack, { noWrap: true, topText: params.row.title, bottomText: params.row.subtitle })))) : (React.createElement(GridCellStack, { topText: params.row.title, bottomText: params.row.subtitle }))),
                    !isNewUi && params.row.freeRoamingNode && React.createElement(FreeRoamingBadge, null),
                    isNewUi && !showAlarmMarker && tabValue === "connected" && (React.createElement(Box, { sx: { width: 20, flexShrink: 0 }, "aria-hidden": true })),
                    showAlarmMarker && sensorAlarms.length > 0 && (React.createElement(SensorAlarmMarker, { alarms: sensorAlarms })),
                    showAlarmMarker && sensorAlarms.length === 0 && (React.createElement(Tooltip, { title: lastAlarmTooltipContent(alarm), arrow: true, placement: "top", enterTouchDelay: 0 },
                        React.createElement(Box, { component: "span", sx: {
                                display: "inline-flex",
                                alignItems: "center",
                                flexShrink: 0,
                                cursor: "default",
                                color: alarm.isAlarmAcknowledged
                                    ? "text.secondary"
                                    : "error.main",
                            }, "aria-label": t("common:grid.lastAlarm") },
                            React.createElement(Icon, { icon: alarm.isAlarmAcknowledged
                                    ? "solar:bell-bold"
                                    : "solar:danger-triangle-bold", width: 20, height: 20 }))))));
            } }),
        ...(isNewUi
            ? [
                {
                    // External temperature reading of the node (API `externalTemperature`).
                    field: "primaryReading",
                    flex: 1,
                    minWidth: READING_COLUMN_WIDTH,
                    align: "center",
                    filterable: false,
                    type: "string",
                    renderCell: (params) => hasEnvironmentReadings(params.row) &&
                        // Shown as the main temperature instead when the internal one is switched off.
                        !params.row.externalAsMain &&
                        !isSensorHidden(params.row.disabledSensors, SensorType.ExternalTemperature) ? (React.createElement(SensorReading, { value: params.row.externalTemperature, unit: "\u00B0C", decimals: 1, tone: "external", inactive: !params.row.isActive, sensorType: SensorType.ExternalTemperature, sensorLabel: params.row.externalTooltip, statuses: params.row.sensorStatuses })) : null,
                },
                {
                    field: "humidity",
                    flex: 1,
                    minWidth: READING_COLUMN_WIDTH,
                    align: "center",
                    filterable: false,
                    type: "string",
                    renderCell: (params) => hasEnvironmentReadings(params.row) &&
                        !isSensorHidden(params.row.disabledSensors, SensorType.Humidity) ? (React.createElement(SensorReading, { value: params.row.humidity, unit: "%", tone: "humidity", inactive: !params.row.isActive, sensorType: SensorType.Humidity, sensorLabel: t("labels.humidity"), statuses: params.row.sensorStatuses })) : null,
                },
            ]
            : []),
        Object.assign(Object.assign({ field: "temperature", headerName: t("grid.temperature") }, layout({ flex: 1, minWidth: 130, align: "right" }, { minWidth: 100 })), { filterable: false, type: "string", renderCell: (params) => {
                // New hardware: the worst of the temperature status and the sensor statuses; older nodes keep their own rule.
                const status = params.row.nodeTypeCode === 1
                    ? "normal"
                    : worstRowStatus(getNodeTemperatureStatus(params.row), params.row.sensorStatuses);
                return isNewUi ? (React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 } },
                    params.row.freeRoamingNode && React.createElement(FreeRoamingBadge, null),
                    React.createElement(TemperatureStatusChip, { status: status, reason: limitReasons(params.row.sensorStatuses, t) }))) : (React.createElement(Box, { sx: { height: "100%", display: "flex", alignItems: "center" } },
                    React.createElement(TemperatureStatusBadge, { status: status })));
            } }),
        Object.assign(Object.assign({ field: "status", headerName: t("grid.status") }, layout({ flex: 1, minWidth: 80, align: "center" }, { flex: 1, minWidth: 115 })), { filterable: true, filterType: "select", filterOptions: ["Active", "Inactive"], type: "string", renderCell: (params) => isNewUi ? (React.createElement(DeviceStatusIndicator, { active: params.row.status === "Active" })) : (React.createElement(StatusIndicator, { variant: params.row.status === "Active" ? "active" : "inactive" })) }),
        Object.assign(Object.assign({ field: "lastUpdate", headerName: t("grid.lastUpdate") }, layout({ flex: 1, minWidth: 100 }, { flex: 1.2, minWidth: 120 })), { filterable: false, type: "string", renderCell: (params) => {
                const isNoCommunication = params.row.nodeBehaviour === NodeBehaviour.NoNodeCommunication;
                const errorColor = theme.palette.error.main;
                return (React.createElement(GridCellStack, Object.assign({ topText: formatUtcToLocalDate(params.row.lastUpdate), bottomText: formatUtcToLocalTime(params.row.lastUpdate) }, (isNoCommunication && {
                    topStyle: { color: errorColor },
                    bottomStyle: { color: errorColor },
                }))));
            } }),
        Object.assign(Object.assign({ field: "actions", headerName: t("grid.actions") }, layout({ width: 280, align: "center" }, { minWidth: 300 })), { filterable: false, type: "string", renderCell: (params) => {
                return (React.createElement(Box, { sx: { display: "flex", gap: 1 } },
                    PermissionChecker.hasPermission(Permission.ACCESSPOINTS_DELETE) &&
                        (tabValue === "disconnected" ? (
                        // Disconnected tab: Show Connect button
                        React.createElement(SButton, { variant: "soft", color: "success", onClick: (e) => {
                                e.stopPropagation();
                                setSelectedNode(params.row);
                                setIsDialogOpen(true);
                            } }, t("actions.couple"))) : (
                        // Connected tab: Show Disconnect button for all nodes
                        React.createElement(SButton, { variant: "soft", color: "error", onClick: (e) => {
                                e.stopPropagation();
                                setSelectedNode(params.row);
                                setIsDialogOpen(true);
                            } }, t("actions.disconnect")))),
                    React.createElement(SButton, { variant: "soft", color: "primary", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/nodes/${params.row.nodeId}/logs/${apId}`);
                        } }, t("actions.logs")),
                    PermissionChecker.hasPermission(Permission.NODES_EDIT) && (React.createElement(SButton, { variant: "outlined", color: "primary", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/nodes/${params.row.id}/edit`);
                        } }, t("actions.settings")))));
            } }),
    ];
    // Server returns the correct page; disconnected list uses simulatedDisconnectedOnly on the API
    const filteredNodes = React.useMemo(() => {
        if (!apId)
            return [];
        return apNodes;
    }, [apNodes, apId]);
    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setSelectedNode(null);
    };
    // Determine if current action is connect or disconnect based on tab
    const isConnectAction = selectedNode && tabValue === "disconnected";
    const handleNodeAction = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedNode || !apId)
            return;
        try {
            if (isConnectAction) {
                // Handle force connect
                yield dispatch(forceConnectNode({
                    nodeId: selectedNode.id,
                    accessPointUid: apId,
                })).unwrap();
                // Small delay to ensure database update is complete, then refresh disconnected list
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    const refreshFilters = cleanFilters(filters);
                    if (debouncedSearchTerm) {
                        refreshFilters.globalSearch = debouncedSearchTerm;
                    }
                    nodesFetchKeyRef.current = null;
                    yield dispatch(getAllNodes({
                        pageNumber: currentPage,
                        pageSize: currentPageSize,
                        filters: Object.assign(Object.assign({}, refreshFilters), { simulatedDisconnectedOnly: true }),
                    })).unwrap();
                }), 200);
            }
            else {
                // Handle force disconnect
                yield dispatch(forceDisconnectNode({
                    nodeId: selectedNode.id,
                })).unwrap();
                // Small delay to ensure database update is complete, then refresh
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    if (tabValue === "disconnected") {
                        const refreshFilters = cleanFilters(filters);
                        if (debouncedSearchTerm) {
                            refreshFilters.globalSearch = debouncedSearchTerm;
                        }
                        yield dispatch(getAllNodes({
                            pageNumber: currentPage,
                            pageSize: currentPageSize,
                            filters: Object.assign(Object.assign({}, refreshFilters), { simulatedDisconnectedOnly: true }),
                        })).unwrap();
                    }
                    else {
                        // Refresh nodes for this AP
                        yield dispatch(getNodesByAccessPointUid({
                            accessPointUid: apId,
                            query: {
                                pageNumber: 1,
                                pageSize: 25,
                                filters: {},
                            },
                        })).unwrap();
                    }
                }), 200);
            }
            handleDialogClose();
        }
        catch (error) {
            console.error(`Failed to ${isConnectAction ? "force connect" : "force disconnect"} node:`, error);
        }
    });
    const handleReplaceNode = (replacementNodeId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedNode || !apId)
            return;
        try {
            yield dispatch(replaceNodeV2({
                replacedNodeId: selectedNode.id,
                replacementNodeId,
                accessPointUid: apId,
            })).unwrap();
            toast.success(t("toasts.nodeReplacedSuccessfully"));
            handleDialogClose();
            const apiFilters = cleanFilters(filters);
            if (debouncedSearchTerm) {
                apiFilters.globalSearch = debouncedSearchTerm;
            }
            yield dispatch(getNodesByAccessPointUid({
                accessPointUid: apId,
                query: {
                    pageNumber: currentPage,
                    pageSize: currentPageSize,
                    filters: apiFilters,
                },
            })).unwrap();
        }
        catch (error) {
            const msg = typeof error === "string"
                ? error
                : error === null || error === void 0 ? void 0 : error.message;
            toast.error(msg || t("toasts.nodeReplaceFailed"));
        }
    });
    const showNodeReplaceTab = !isConnectAction && PermissionChecker.hasPermission(Permission.NODES_EDIT);
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t(""), showBackButton: true }),
        React.createElement(PageContent, { sx: { pb: 1 } },
            singleLoading && !effectiveAp ? (React.createElement(APDetailsCardSkeleton, null)) : (React.createElement(APDetailsCard, { name: (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointName) || t("actions.unknownAp"), deviceId: (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointUID) || apId || "", apId: apId, status: (effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.isActive) ? "active" : "inactive", lastUpdatedAt: effectiveAp ? getLatestApActivityIso(effectiveAp) : undefined, address: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.address, place: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.place, calibrationDate: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.calibrationDate, onSettingsClick: PermissionChecker.hasPermission(Permission.ACCESSPOINTS_EDIT)
                    ? () => navigate(`/dashboard/${apId}/edit`)
                    : undefined })),
            React.createElement(GridFilters
            // New hardware: just "Nodes"; the count is in the pagination.
            , { 
                // New hardware: just "Nodes"; the count is in the pagination.
                title: isNewUi
                    ? t("pages.nodes")
                    : `${(_b = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _b !== void 0 ? _b : filteredNodes.length} ${t("pages.nodes")}`, loading: nodesLoading, columns: columns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange, rightSection: React.createElement(Tabs, { value: tabValue, onChange: (_, v) => {
                        if (isDialogOpen)
                            return;
                        setTabValue(v);
                        setCurrentPage(1);
                    }, sx: { minHeight: 40 } },
                    React.createElement(Tab, { value: "connected", label: React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                            React.createElement(ConnectedIcon, { width: 20, height: 20 }),
                            React.createElement("span", null, t("status.connected"))) }),
                    React.createElement(Tab, { value: "disconnected", label: React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                            React.createElement(DisconnectedIcon, { width: 20, height: 20 }),
                            React.createElement("span", null, t("status.disconnected"))) })) }),
            React.createElement(PrimaryGrid, Object.assign({ rows: nodesLoading ? [] : filteredNodes, columns: columns, loading: nodesLoading, heightVariant: "dashboard", checkboxSelection: false, autoHeight: true, paginationMode: "server" }, (isNewUi && {
                rowsPrefix: tabValue === "connected" ? (React.createElement(APReadingsSection, { cardView: isCardView, columns: columns, isActive: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.isActive, deviceId: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointUID, apNodeId: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.apNodeId, temperature: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.temperature, co2: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.cO2, humidity: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.humidity, lowTemperature: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.lowTemperature, highTemperature: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.highTemperature, lastAlarms: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.lastAlarms, sensorStatuses: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.sensorStatuses, disabledSensors: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.disabledSensors, lastCommunicationAt: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.lastApCommunication })) : undefined,
                minContentWidth: getDeviceDetailsMinWidth(columns),
                renderRow: (row) => (React.createElement(Box, { sx: { mb: 1 } },
                    React.createElement(DeviceDetailsRow, { columns: columns, renderCell: (col) => {
                            var _a, _b;
                            return (_b = (_a = col.renderCell) === null || _a === void 0 ? void 0 : _a.call(col, {
                                row,
                                value: row[col.field],
                                field: col.field,
                                id: row.id,
                            })) !== null && _b !== void 0 ? _b : null;
                        } }))),
                renderSkeletonRow: () => (React.createElement(Skeleton, { variant: "rounded", height: 88, sx: { borderRadius: "32px", mb: 1 } })),
            }), { renderCard: (row) => {
                    const CardComponent = isNewUi
                        ? APDetailsNodeCard
                        : LegacyAPDetailsNodeCard;
                    return (React.createElement(CardComponent, { row: row, tabValue: tabValue, onAction: (node) => {
                            setSelectedNode(node);
                            setIsDialogOpen(true);
                        }, onLogs: (nodeId) => navigate(`/nodes/${nodeId}/logs/${apId}`), onSettings: (id) => navigate(`/nodes/${id}/edit`) }));
                }, rowCount: (_c = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _c !== void 0 ? _c : 0, paginationModel: {
                    page: currentPage - 1,
                    pageSize: currentPageSize,
                }, onPaginationModelChange: (model) => {
                    if (model.pageSize !== currentPageSize) {
                        setCurrentPageSize(model.pageSize);
                        setCurrentPage(1);
                    }
                    else if (model.page + 1 !== currentPage) {
                        setCurrentPage(model.page + 1);
                    }
                }, pageSizeOptions: [25, 50] }))),
        React.createElement(NodeActionsDialog, { isOpen: isDialogOpen, onClose: handleDialogClose, onConfirm: handleNodeAction, selectedNode: selectedNode, action: isConnectAction ? "connect" : "disconnect", accessPointName: effectiveAp === null || effectiveAp === void 0 ? void 0 : effectiveAp.accessPointName, showReplaceTab: showNodeReplaceTab, onReplace: showNodeReplaceTab ? handleReplaceNode : undefined })));
};
